import type { ConversationUpdateMessageDatagramBuf } from "~/classes/generated/message"
import type { ConversationUpdateResponseDatagramBuf } from "~/classes/generated/response"

import { StaticConversation } from "~/classes/conversation/StaticConversation"
import { DatagramBuf } from "~/classes/generated/datagram"
import { ConversationUpdateRequestDatagramBuf, RequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addConversationToBuf, parseConversation } from "~/utils/flatbuffers/conversation"
import { addRequestIdToBuf, parseDatagramError, parseMessageId, parseRequestId, parseSenderId } from "~/utils/flatbuffers/datagram"
import { parseConversationId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const console = useLogger("conversation/update")

export function sendConversationUpdateRequests(requests: ConversationUpdateRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()

  const builder = new DatagramBufferBuilder(64)
  const updateRequests = []
  for (const request of requests) {
    const requestId = addRequestIdToBuf(builder, request.requestId)
    const conversation = addConversationToBuf(builder, request.conversation)

    ConversationUpdateRequestDatagramBuf.startConversationUpdateRequestDatagramBuf(builder)
    ConversationUpdateRequestDatagramBuf.addRequestId(builder, requestId)
    ConversationUpdateRequestDatagramBuf.addOffline(builder, request.offline)
    ConversationUpdateRequestDatagramBuf.addConversation(builder, conversation)
    const updateRequest = ConversationUpdateRequestDatagramBuf.endConversationUpdateRequestDatagramBuf(builder)

    RequestDatagramBuf.startRequestDatagramBuf(builder)
    RequestDatagramBuf.addConversationUpdateRequest(builder, updateRequest)
    updateRequests.push(RequestDatagramBuf.endRequestDatagramBuf(builder))
  }

  const update = DatagramBuf.createRequestVector(builder, updateRequests)
  DatagramBuf.startDatagramBuf(builder)
  DatagramBuf.addRequest(builder, update)
  builder.finish(DatagramBuf.endDatagramBuf(builder))
  sendWorkerDatagramBuf(builder.asUint8Array())
}

/** Parse a Flatbuffers representation of a response */
export function handleConversationUpdateResponseDatagramBuf(datagram: ConversationUpdateResponseDatagramBuf) {
  onConversationUpdateResponse({
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    conversation: maybe(parseConversation)(datagram.conversation()),
    offline: datagram.offline(),
    conversationId: parseConversationId(datagram.conversationId()!),
  })
}

export function onConversationUpdateResponse(response: ConversationUpdateResponseDatagram) {
  const { emitEntityEvent, emitInterfaceEvent } = useEvents()

  console.debug("Received conversation update", response)

  emitInterfaceEvent("conversationUpdateResponse", response, response.requestId)

  if (response.error)
    return onConversationUpdateErrored(response.error)
  if (!response.conversation)
    throw new Error("No conversation in update response")

  const iConversation = new StaticConversation(response.conversation)
  emitEntityEvent("conversationProperties", iConversation.id, iConversation.inner(), response.offline)
}

function onConversationUpdateErrored(error: DatagramError) {
  const { notifyDatagramError } = useAlerts()

  return notifyDatagramError(error, "Conversation update request was unsuccessful")
}

export function handleConversationUpdateMessageDatagramBuf(datagram: ConversationUpdateMessageDatagramBuf) {
  onConversationUpdateMessage({
    messageId: parseMessageId(datagram.messageId()!),
    senderId: parseSenderId(datagram.senderId()!),
    conversation: parseConversation(datagram.conversation()!),
  })
}

export function onConversationUpdateMessage(message: ConversationUpdateMessageDatagram) {
  const { emitEntityEvent } = useEvents()

  console.debug("Received conversation update message", clone(message))

  const iConversation = new StaticConversation(message.conversation)
  emitEntityEvent("conversationProperties", iConversation.id, iConversation.inner(), false)
}
